import { useIntl } from 'react-intl'
import React, { useState } from 'react'

import { getCookie } from '../common/cookie'
import PickerTool from './Picker'
import { useGetStandardRegions, useRequestCommon } from '../../services/hooks'
import { isProduction } from '../../utils/env'

const GLOBAL_API_BASE_URL = isProduction() ? 'https://api.padi.com' : 'https://api.global-np.padi.com'

// 繁体→简体小映射：PADI 名称可能混繁体（如「內蒙古自治區」），大陆省市展示固定简体中文。
// 仅覆盖 PADI 参考数据中实际出现的繁体字；未覆盖字原样保留（后端归一化还有更全的映射兜底）
const CN_TRADITIONAL_MAP = {
  內: '内',
  廣: '广',
  寧: '宁',
  陝: '陕',
  貴: '贵',
  雲: '云',
  遼: '辽',
  龍: '龙',
  蘇: '苏',
  慶: '庆',
  臺: '台',
  東: '东',
  頭: '头',
  烏: '乌',
  倫: '伦',
  貝: '贝',
  爾: '尔',
  蘭: '兰',
  興: '兴',
  錫: '锡',
  滿: '满',
  紮: '扎',
  額: '额',
  納: '纳',
  豐: '丰',
  鎮: '镇',
  連: '连',
  區: '区',
  灣: '湾',
  島: '岛',
}
const simplifyCn = (s) =>
  (s || '').replace(
    /[內廣寧陝貴雲遼龍蘇慶臺東頭烏倫貝爾蘭興錫滿紮額納豐鎮連區灣島]/g,
    (c) => CN_TRADITIONAL_MAP[c] || c
  )

/**
 * 从 PADI 参考接口的混合名称中提取中文并繁→简。
 * 实测结构（2026-08-17 api.padi.com）：
 * - 省（/states）仅 { stateId, name, code }，无 names；name 为混合串
 *   "English - 中文 拼音"，分隔符不统一（" - " 或 "-"）、中文可能繁体、可能带拼音/异常字符
 * - 市（/states/{id}/cities）names.zh-cn 为 "拼音 - 中文"（简体为主，个别繁体）
 * 该 label 会作为下单参数传给后端归一化匹配运费档，提取后只剩干净中文，减少
 * 后端「60 字繁简映射未覆盖/非 CJK 拒单」的风险面。
 */
export const extractCnLabel = (raw) => {
  if (typeof raw !== 'string' || !raw) return raw
  // 中文名位于最后一个 "-" 之后；无 "-"（如异常形态 "Taiwan Province5"）则取整串
  const source = raw.includes('-') ? raw.split('-').pop() : raw
  const cjk = (source.match(/[㐀-䶿一-鿿]/g) || []).join('')
  // 提取不到中文时回退原值，避免空 label 阻断选择
  return cjk ? simplifyCn(cjk) : raw
}

const useGetChinaProvinces = () => {
  const language = getCookie('language')

  return useRequestCommon(
    () => {
      const url = `${GLOBAL_API_BASE_URL}/r/references/Countries/51/states`

      return fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          return response.json()
        })
        .then((result) => {
          // 港澳台不是大陆收货省市，从选项中剔除：label 已清洗为简体中文，
          // 英文形态（如 "Taiwan Province5" 提取不到中文时）按原始 name 兜底过滤
          const hiddenRegionKeys = ['香港', '澳门', '台湾', 'hong kong', 'macao', 'macau', 'taiwan']
          return result
            .filter((item) => {
              const label = extractCnLabel(item.name)
              return !hiddenRegionKeys.some(
                (key) => label.toLowerCase().includes(key) || (item.name || '').toLowerCase().includes(key)
              )
            })
            .map((item) => ({
              label: extractCnLabel(item.name),
              value: item.stateId,
            }))
        })
    },
    {
      manual: false,
      refreshDeps: [language],
    },
    true
  )
}

const useGetChinaCities = (provinceId) => {
  const language = getCookie('language')

  return useRequestCommon(
    () => {
      if (!provinceId || provinceId === '-1') {
        return []
      }

      const url = `${GLOBAL_API_BASE_URL}/r/references/Countries/51/states/${provinceId}/cities`

      return fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          return response.json()
        })
        .then((result) => {
          return result.map((item) => {
            // 大陆市区 label 固定简体中文（与省 label 一致）：按页面语言取 en-us 会得到纯拼音
            // （如 "DongChengQu"），zh-hk 经 extractCnLabel 繁转简后与 zh-cn 等价，统一取 zh-cn
            return {
              label: extractCnLabel(item.names['zh-cn']),
              value: item.areaId,
              englishName: item.names['en-us'],
            }
          })
        })
    },
    {
      manual: false,
      refreshDeps: [provinceId, language],
    },
    true
  )
}

export default ({
  form,
  countryIdFormKey = 'countryId',
  stateIdFormKey = 'stateId',
  cityIdFormKey = 'cityId',
  cityFormKey = 'city',
  required,
  disabled,
}) => {
  const { formatMessage } = useIntl()
  const [countryId, setCountryId] = useState(form.getFieldValue(countryIdFormKey))
  const [provinceId, setProvinceId] = useState(form.getFieldValue(stateIdFormKey))
  const [regionData, setRegionData] = useState([])
  const language = getCookie('language')

  const { data: chinaProvinces, loading: provincesLoading } = useGetChinaProvinces()
  const { data: chinaCities, loading: citiesLoading } = useGetChinaCities(provinceId)

  const onCountryIdChange = (value) => {
    if (value !== '51') {
      form.setFieldsValue({
        [stateIdFormKey]: '-1',
        [cityIdFormKey]: '-1',
        [cityFormKey]: undefined,
      })
      setProvinceId('-1')
    }
    setCountryId(value)
  }

  const onProvinceIdChange = (value) => {
    form.setFieldsValue({ [cityIdFormKey]: '-1' })
    setProvinceId(value)
  }

  const { loading } = useGetStandardRegions({
    onSuccess: (data) => {
      const formatData = data.map((item) => {
        return {
          label: item.region,
          value: item.id,
        }
      })
      setRegionData(formatData)
    },
  })

  return (
    <>
      <PickerTool
        form={form}
        name={countryIdFormKey}
        disabled={disabled}
        placeholder={formatMessage({ id: 'dm-select-region-error' })}
        rules={
          required && [
            {
              required: true,
              message: formatMessage({ id: 'form-region-empty-error' }),
            },
          ]
        }
        onOk={onCountryIdChange}
        data={regionData}
        label={formatMessage({ id: 'setMobile-regions' })}
      />
      {countryId === '51' && (
        <PickerTool
          form={form}
          name={stateIdFormKey}
          disabled={disabled}
          placeholder={formatMessage({ id: 'form-stateid-hint' })}
          data={chinaProvinces || []}
          rules={
            required && [
              {
                required: true,
                message: formatMessage({ id: 'form-stateid-empty-error' }),
              },
            ]
          }
          label={formatMessage({ id: 'common-Province' })}
          onOk={onProvinceIdChange}
        />
      )}

      {countryId === '51' && (
        <PickerTool
          form={form}
          name={cityIdFormKey}
          disabled={disabled || !provinceId || provinceId === '-1'}
          placeholder={formatMessage({ id: 'form-city-hint' })}
          data={chinaCities || []}
          rules={required && [{ required: true, message: formatMessage({ id: 'form-city-empty-error' }) }]}
          label={formatMessage({ id: 'common-City' })}
          onOk={(value) => {
            const selectedCity = chinaCities.find((city) => city.value === parseInt(value))
            if (selectedCity) {
              form.setFieldsValue({
                [cityIdFormKey]: value,
                [cityFormKey]: selectedCity.englishName,
              })
            }
          }}
        />
      )}
    </>
  )
}

export { useGetChinaProvinces, useGetChinaCities }
